(() => {
  'use strict';

  const routes = window.apexRoutes || {};
  const locale = window.apexLocale || {};
  const settings = window.apexSettings || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  function setBodyLock(locked) {
    document.body.classList.toggle('is-locked', Boolean(locked));
  }

  function showToast(message) {
    const toast = qs('[data-apex-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(showToast.timeout);
    showToast.timeout = setTimeout(() => { toast.hidden = true; }, 3200);
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  let privacyBannerLoading;
  function loadPrivacyBanner() {
    if (window.privacyBanner?.showPreferences) return Promise.resolve(window.privacyBanner);
    if (!privacyBannerLoading) {
      privacyBannerLoading = new Promise((resolve, reject) => {
        const source = 'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';
        const existing = document.querySelector(`script[src="${source}"]`);
        const script = existing || document.createElement('script');
        const finish = () => {
          clearTimeout(timeout);
          script.removeEventListener('load', finish);
          script.removeEventListener('error', finish);
          if (window.privacyBanner?.showPreferences) resolve(window.privacyBanner);
          else {
            if (!existing) script.remove();
            reject(new Error('Cookie preferences unavailable'));
          }
        };
        const timeout = setTimeout(finish, 10000);
        script.addEventListener('load', finish);
        script.addEventListener('error', finish);
        if (!existing) {
          script.src = source;
          script.async = true;
          document.head.appendChild(script);
        }
      }).catch((error) => { privacyBannerLoading = null; throw error; });
    }
    return privacyBannerLoading;
  }

  async function openCookiePreferences(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      const banner = await loadPrivacyBanner();
      await banner.showPreferences();
    } catch (_) {
      showToast('Cookie settings could not be opened. Please reload the page and try again.');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  class ApexHeader extends HTMLElement {
    connectedCallback() {
      this.menuButton = qs('[data-menu-toggle]', this);
      this.menu = qs('[data-mobile-menu]', this);
      this.menuCloseButton = qs('[data-menu-close]:not(.mobile-menu__backdrop)', this.menu);
      this.categoriesButton = qs('[data-categories-toggle]', this);
      this.categories = qs('[data-header-categories]', this);
      this.searchButton = qs('[data-search-toggle]', this);
      this.search = qs('[data-header-search]', this);
      this.countryButton = qs('[data-country-toggle]', this);
      this.country = qs('[data-header-country]', this);
      this.surpriseLinks = qsa('[data-surprise-me]', this);

      this.onMenuToggle = () => this.toggleMenu();
      this.onCategoriesToggle = () => this.toggleCategories();
      this.onSearchToggle = () => this.toggleUtility('search');
      this.onCountryToggle = () => this.toggleUtility('country');
      this.onSurpriseClick = (event) => this.openRandomDesign(event);
      this.onSurprisePreload = () => this.loadSurpriseProducts();
      this.onHeaderClick = (event) => {
        if (event.target.closest('[data-menu-close]')) this.closeMenu(true);
        if (event.target.closest('[data-mobile-menu] a')) this.closeMenu(false);
      };
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) {
          this.closeCategories();
          this.closeUtilities();
        }
      };
      this.onDocumentKeydown = (event) => {
        if (event.key === 'Escape') {
          const hadOpenPanel = this.isMenuOpen()
            || Boolean(this.categories && !this.categories.hidden)
            || Boolean(this.search && !this.search.hidden)
            || Boolean(this.country && !this.country.hidden);
          this.closeAll(true);
          if (hadOpenPanel) event.preventDefault();
        }
        if (event.key === 'Tab' && this.isMenuOpen()) this.trapMenuFocus(event);
      };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.classList.toggle('is-scrolled', window.scrollY > 18);
          this.scrollFrame = null;
        });
      };

      this.menuButton?.addEventListener('click', this.onMenuToggle);
      this.categoriesButton?.addEventListener('click', this.onCategoriesToggle);
      this.searchButton?.addEventListener('click', this.onSearchToggle);
      this.countryButton?.addEventListener('click', this.onCountryToggle);
      this.surpriseLinks.forEach((link) => {
        link.addEventListener('click', this.onSurpriseClick);
        link.addEventListener('mouseenter', this.onSurprisePreload, { once: true });
        link.addEventListener('focus', this.onSurprisePreload, { once: true });
      });
      this.addEventListener('click', this.onHeaderClick);
      qs('[data-country-select]', this)?.addEventListener('change', (event) => event.target.form?.submit());
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.classList.toggle('is-scrolled', window.scrollY > 18);
    }

    disconnectedCallback() {
      this.menuButton?.removeEventListener('click', this.onMenuToggle);
      this.categoriesButton?.removeEventListener('click', this.onCategoriesToggle);
      this.searchButton?.removeEventListener('click', this.onSearchToggle);
      this.countryButton?.removeEventListener('click', this.onCountryToggle);
      this.surpriseLinks?.forEach((link) => {
        link.removeEventListener('click', this.onSurpriseClick);
        link.removeEventListener('mouseenter', this.onSurprisePreload);
        link.removeEventListener('focus', this.onSurprisePreload);
      });
      this.removeEventListener('click', this.onHeaderClick);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('scroll', this.onScroll);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    isMenuOpen() {
      return Boolean(this.menu?.classList.contains('is-open'));
    }

    toggleMenu() {
      if (!this.menu) return;
      if (this.isMenuOpen()) {
        this.closeMenu(true);
        return;
      }
      this.closeCategories();
      this.closeUtilities();
      this.classList.add('has-mobile-menu-open');
      this.menu.classList.add('is-open');
      this.menu.setAttribute('aria-hidden', 'false');
      this.menuButton?.setAttribute('aria-expanded', 'true');
      setBodyLock(true);
      requestAnimationFrame(() => this.menuCloseButton?.focus());
    }

    closeMenu(restoreFocus = false) {
      if (!this.menu) return;
      const wasOpen = this.isMenuOpen();
      this.menu.classList.remove('is-open');
      this.classList.remove('has-mobile-menu-open');
      this.menu.setAttribute('aria-hidden', 'true');
      this.menuButton?.setAttribute('aria-expanded', 'false');
      qsa('details[open]', this.menu).forEach((details) => details.removeAttribute('open'));
      setBodyLock(false);
      if (restoreFocus && wasOpen) this.menuButton?.focus();
    }

    toggleCategories() {
      if (!this.categories) return;
      const willOpen = this.categories.hidden;
      this.closeMenu(false);
      this.closeUtilities();
      this.closeCategories();
      if (willOpen) {
        this.categories.hidden = false;
        this.categoriesButton?.setAttribute('aria-expanded', 'true');
      }
    }

    closeCategories() {
      if (this.categories) this.categories.hidden = true;
      this.categoriesButton?.setAttribute('aria-expanded', 'false');
    }

    toggleUtility(target) {
      const map = {
        search: [this.search, this.searchButton],
        country: [this.country, this.countryButton]
      };
      const [panel, button] = map[target] || [];
      if (!panel) return;
      const willOpen = panel.hidden;
      this.closeMenu(false);
      this.closeCategories();
      this.closeUtilities();
      if (willOpen) {
        panel.hidden = false;
        button?.setAttribute('aria-expanded', 'true');
        if (target === 'search') qs('input[type="search"]', panel)?.focus();
      }
    }

    closeUtilities() {
      [[this.search, this.searchButton], [this.country, this.countryButton]].forEach(([panel, button]) => {
        if (panel) panel.hidden = true;
        button?.setAttribute('aria-expanded', 'false');
      });
    }

    closeAll(restoreFocus = false) {
      const categoriesWasOpen = Boolean(this.categories && !this.categories.hidden);
      this.closeMenu(restoreFocus);
      this.closeCategories();
      this.closeUtilities();
      if (restoreFocus && categoriesWasOpen) this.categoriesButton?.focus();
    }

    loadSurpriseProducts() {
      if (this.surpriseProductsPromise) return this.surpriseProductsPromise;
      try {
        const cached = JSON.parse(window.sessionStorage.getItem('apex:surprise-products') || 'null');
        if (cached?.savedAt && Date.now() - cached.savedAt < 15 * 60 * 1000 && Array.isArray(cached.products) && cached.products.length) {
          this.surpriseProductsPromise = Promise.resolve(cached.products);
          return this.surpriseProductsPromise;
        }
      } catch (_) {}
      const endpoint = this.surpriseLinks?.[0]?.dataset.surpriseEndpoint;
      if (!endpoint) return Promise.resolve([]);
      this.surpriseProductsPromise = fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Surprise endpoint returned ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const payload = qs('[data-surprise-products]', documentFragment);
          const values = payload ? JSON.parse(payload.textContent || '[]') : [];
          const productPaths = values
            .map((value) => {
              try {
                const url = new URL(value, window.location.origin);
                return url.origin === window.location.origin && url.pathname.includes('/products/')
                  ? `${url.pathname}${url.search}`
                  : null;
              } catch (_) {
                return null;
              }
            })
            .filter(Boolean);
          const products = [...new Set(productPaths)];
          if (products.length) {
            try {
              window.sessionStorage.setItem('apex:surprise-products', JSON.stringify({
                savedAt: Date.now(),
                products
              }));
            } catch (_) {}
          }
          return products;
        })
        .catch(() => []);
      return this.surpriseProductsPromise;
    }

    async openRandomDesign(event) {
      const link = event.currentTarget;
      if (!link || link.getAttribute('aria-busy') === 'true') return;
      event.preventDefault();
      link.setAttribute('aria-busy', 'true');
      const fallback = link.href;
      const products = await this.loadSurpriseProducts();
      if (!products.length) {
        window.location.assign(fallback);
        return;
      }

      const currentPath = window.location.pathname.replace(/\/$/, '');
      let previousPath = '';
      try { previousPath = window.sessionStorage.getItem('apex:last-surprise') || ''; } catch (_) {}
      const freshProducts = products.filter((path) => {
        const normalizedPath = path.split('?')[0].replace(/\/$/, '');
        return normalizedPath !== currentPath && normalizedPath !== previousPath;
      });
      const choices = freshProducts.length ? freshProducts : products;
      const destination = choices[Math.floor(Math.random() * choices.length)];
      try { window.sessionStorage.setItem('apex:last-surprise', destination.split('?')[0].replace(/\/$/, '')); } catch (_) {}
      window.location.assign(destination);
    }

    trapMenuFocus(event) {
      const focusable = qsa('a[href], button:not([disabled]), summary', this.menu)
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class ApexCartDrawer extends HTMLElement {
    connectedCallback() {
      qsa('[data-cart-drawer-close]', this).forEach((button) => button.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.hidden) this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    open() {
      this.hidden = false;
      setBodyLock(true);
      qs('[data-cart-drawer-close]', this)?.focus();
      qsa('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    }

    close() {
      this.hidden = true;
      setBodyLock(false);
      qsa('[data-cart-drawer-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    }
  }

  function updateCartCount(cart) {
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });
  }

  async function refreshCartSections(cart, { openDrawer = false, mainSectionId = '', signal } = {}) {
    const root = routes.root || '/';
    const sectionIds = ['cart-drawer'];
    if (mainSectionId) sectionIds.push(mainSectionId);
    const sectionUrl = new URL(mainSectionId ? window.location.href : root, window.location.origin);
    sectionUrl.hash = '';
    sectionUrl.search = '';
    sectionUrl.searchParams.set('sections', sectionIds.join(','));
    let sections = cart.sections;
    if (!sections || sectionIds.some(id => !sections[id])) {
      const sectionResponse = await fetch(sectionUrl, { headers: { Accept: 'application/json' }, signal, cache: 'no-store' });
      if (!sectionResponse.ok) throw new Error(locale.cartUpdateError || 'The cart could not be updated.');
      sections = await sectionResponse.json();
    }

    updateCartCount(cart);

    if (sections['cart-drawer']) {
      const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const replacement = qs('[data-cart-drawer]', parsed);
      const current = qs('[data-cart-drawer]');
      if (replacement && current) current.replaceWith(replacement);
    }

    if (mainSectionId && sections[mainSectionId]) {
      const parsed = new DOMParser().parseFromString(sections[mainSectionId], 'text/html');
      const replacement = qs('[data-main-cart]', parsed);
      const current = qs('[data-main-cart]');
      if (replacement && current) current.replaceWith(replacement);
    }

    if (openDrawer) requestAnimationFrame(() => qs('[data-cart-drawer]')?.open());
    return cart;
  }

  async function refreshCartDrawer(openAfter = false) {
    const root = routes.root || '/';
    const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!cartResponse.ok) throw new Error(locale.cartUpdateError || 'The cart could not be updated.');
    const cart = await cartResponse.json();
    return refreshCartSections(cart, { openDrawer: openAfter });
  }

  async function cleanLegacyCartDetails() {
    if (!qs('[data-apex-legacy-properties]') || !window.apexCartDetails) return;
    const regions = qsa('[data-main-cart], [data-cart-drawer], [data-product-form]');
    const previous = regions.map(region => ({ region, inert: region.inert }));
    const signal = AbortSignal.timeout(12000);
    const mainSectionId = qs('[data-main-cart]')?.dataset.sectionId || '';
    const sections = ['cart-drawer', mainSectionId].filter(Boolean);
    // Prevent simultaneous quantity/add operations while old line keys change.
    regions.forEach(region => { region.inert = true; region.setAttribute('aria-busy', 'true'); });
    try {
      const response = await fetch(`${routes.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, signal });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await window.apexCartDetails.migrateCart(await response.json(), async payload => {
        const result = await fetch(routes.cartChange || '/cart/change.js', {
          method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, sections, sections_url: window.location.pathname }), signal
        });
        if (!result.ok) throw new Error('Cart details unavailable');
        return result.json();
      });
      await refreshCartSections(cart, {
        openDrawer: qs('[data-cart-drawer]')?.hidden === false,
        mainSectionId, signal
      });
    } catch (_) {
      // Cosmetic cleanup must not prevent checkout. Refresh changed keys after
      // a partial migration; a subsequent visit can finish the remaining lines.
      try {
        const recoverySignal = AbortSignal.timeout(4000);
        const response = await fetch(`${routes.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: recoverySignal });
        if (response.ok) await refreshCartSections(await response.json(), { mainSectionId: qs('[data-main-cart]')?.dataset.sectionId || '', signal: recoverySignal });
      } catch (_) { /* Keep the existing cart usable during a network failure. */ }
    } finally {
      previous.forEach(({ region, inert }) => { region.inert = inert; region.removeAttribute('aria-busy'); });
    }
  }

  async function changeCartLine(line, quantity) {
    const lineKey = line?.dataset.lineKey;
    if (!lineKey) return;
    const nextQuantity = Math.max(0, Math.floor(Number(quantity) || 0));
    const context = line.dataset.cartContext;
    const mainSectionId = line.dataset.sectionId || '';
    line.setAttribute('aria-busy', 'true');
    qsa('button,input', line).forEach((control) => { control.disabled = true; });

    try {
      const response = await fetch(routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: nextQuantity })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || locale.cartUpdateError);
      await refreshCartSections(cart, { openDrawer: context === 'drawer', mainSectionId });
      showToast(locale.cartUpdated || 'Cart updated.');
    } catch (error) {
      line.removeAttribute('aria-busy');
      qsa('button,input', line).forEach((control) => { control.disabled = false; });
      showToast(error.message || locale.cartUpdateError || 'The cart could not be updated.');
    }
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        if (this.input.value.trim().length < 2) return this.close();
        this.timer = setTimeout(() => this.search(), 250);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) return this.close();
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const query = window.apexDesignSearch?.queryForTerm(term) || term;
      const endpoint = query === term
        ? `${routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&section_id=predictive-search`
        : `${routes.search || '/search'}?q=${encodeURIComponent(query)}&type=product&section_id=predictive-search`;
      try {
        const response = await fetch(endpoint, { signal: controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        if (controller.signal.aborted || this.input.value.trim() !== term) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = qs('#shopify-section-predictive-search', parsed);
        this.results.innerHTML = section?.innerHTML || '';
        this.input.setAttribute('aria-expanded', this.results.innerHTML ? 'true' : 'false');
      } catch (error) {
        if (error.name !== 'AbortError' && this.input.value.trim() === term) this.close();
      }
    }

    close() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.results.innerHTML = '';
      this.input?.setAttribute('aria-expanded', 'false');
    }
  }

  class ApexProductConfigurator extends HTMLElement {
    connectedCallback() {
      this.country = String(this.dataset.country || '').toUpperCase();
      this.fallback = String(this.dataset.marketFallback || 'WORLD').toUpperCase();
      this.panels = qsa('[data-offering-panel]', this);
      this.familyLinks = qsa('[data-family-option]', this);
      this.fitLinks = qsa('[data-fit-option]', this);
      this.sideLinks = qsa('[data-side-option]', this);
      this.colorLinks = qsa('[data-color-option]', this);
      this.inkLinks = qsa('[data-ink-option]', this);
      this.unavailable = qs('[data-market-unavailable]', this);
      this.ratingBlocks = qs('[data-product-rating-blocks]', this.closest('.main-product'));
      if (!this.panels.length) return;

      const current = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      const roleParam = new URLSearchParams(window.location.search).get('role');
      const params = this.dataset.coupleRole && roleParam !== this.dataset.coupleRole ? new URLSearchParams() : new URLSearchParams(window.location.search);
      const requestedFamily = params.get('family');
      this.family = requestedFamily || current.dataset.family;
      this.color = params.get('color') || current.dataset.color || 'black';
      this.ink = params.get('ink') || current.dataset.ink || '';
      this.fit = params.get('fit') || (requestedFamily ? this.firstFit(this.family, current.dataset.side, this.color) : current.dataset.fit);
      this.side = params.get('side') || current.dataset.side;

      this.onChoiceClick = (event) => this.chooseOffering(event);
      this.onChoiceIntent = (event) => {
        const link = event.target.closest('[data-family-option], [data-fit-option], [data-side-option], [data-color-option], [data-ink-option]');
        if (!link || link.classList.contains('is-disabled') || link.contains(event.relatedTarget)) return;
        clearTimeout(this.prefetchTimer);
        const pathname = new URL(link.href, window.location.origin).pathname;
        const panel = this.eligiblePanels().find(item => new URL(item.dataset.productUrl, window.location.origin).pathname === pathname);
        if (panel) this.prefetchTimer = setTimeout(() => this.loadPanel(panel), 120);
      };
      this.addEventListener('click', this.onChoiceClick);
      this.addEventListener('pointerover', this.onChoiceIntent);
      this.addEventListener('focusin', this.onChoiceIntent);

      this.handlePopState = () => {
        const nextParams = new URLSearchParams(window.location.search);
        if (this.dataset.coupleRole && nextParams.get('role') !== this.dataset.coupleRole) return;
        const nextFamily = nextParams.get('family');
        this.family = nextFamily || current.dataset.family;
        this.color = nextParams.get('color') || current.dataset.color || 'black';
        this.ink = nextParams.get('ink') || current.dataset.ink || '';
        this.fit = nextParams.get('fit') || (nextFamily ? this.firstFit(this.family, current.dataset.side, this.color) : current.dataset.fit);
        this.side = nextParams.get('side') || this.firstSide(this.fit, this.color);
        this.activate(false);
      };
      window.addEventListener('popstate', this.handlePopState);

      this.activate(false);
    }

    refreshLinks() {
      this.familyLinks = qsa('[data-family-option]', this);
      this.fitLinks = qsa('[data-fit-option]', this);
      this.sideLinks = qsa('[data-side-option]', this);
      this.colorLinks = qsa('[data-color-option]', this);
      this.inkLinks = qsa('[data-ink-option]', this);
    }

    chooseOffering(event) {
      const link = event.target.closest('[data-family-option], [data-fit-option], [data-side-option], [data-color-option], [data-ink-option]');
      if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (link.classList.contains('is-disabled')) return;
      let fallbackUrl = link.href;
      if (link.hasAttribute('data-family-option')) {
        this.family = link.dataset.family;
        const currentFitPanel = this.panels.find(panel => panel.dataset.family === this.family && panel.dataset.fit === this.fit && panel.dataset.color === this.color);
        if (!currentFitPanel) this.fit = this.firstFit(this.family, this.side, this.color);
        if (!this.findPanel(this.fit, this.side, this.color)) this.side = this.firstSide(this.fit, this.color);
      } else if (link.hasAttribute('data-fit-option')) {
        this.family = link.dataset.family;
        this.fit = link.dataset.fit;
        if (!this.findPanel(this.fit, this.side, this.color)) this.side = this.firstSide(this.fit, this.color);
      } else if (link.hasAttribute('data-color-option')) {
        this.color = link.dataset.color;
      } else if (link.hasAttribute('data-ink-option')) {
        const matching = this.findInkPanel(link.dataset.ink);
        if (!matching) return;
        this.ink = link.dataset.ink;
        this.color = matching.dataset.color;
        fallbackUrl = matching.dataset.productUrl;
      } else {
        this.side = link.dataset.side;
      }
      this.activate(true, fallbackUrl);
    }

    async loadPanel(panel) {
      if (panel.dataset.panelDeferred !== 'true') return true;
      if (panel.contentPromise) return panel.contentPromise;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      panel.contentPromise = (async () => {
        try {
          const url = new URL(panel.dataset.productUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { headers: { Accept: 'text/html' }, signal: controller.signal });
          if (!response.ok) throw new Error('Product section unavailable');
          const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
          const loaded = qsa('[data-offering-panel]', fragment).find(item => item.dataset.productId === panel.dataset.productId && item.dataset.panelDeferred !== 'true');
          if (!loaded || !qs('apex-product-form', loaded)) throw new Error('Matching garment unavailable');
          panel.dataset.needsVariantHydration = loaded.dataset.needsVariantHydration || 'false';
          panel.innerHTML = loaded.innerHTML;
          panel.dataset.panelDeferred = 'false';
          return true;
        } catch (_) {
          panel.contentPromise = null;
          return false;
        } finally {
          clearTimeout(timeout);
        }
      })();
      return panel.contentPromise;
    }

    placeRatingBlocks(panel) {
      const target = qs('[data-product-rating-slot]', panel);
      if (target && this.ratingBlocks && this.ratingBlocks.parentElement !== target) target.append(this.ratingBlocks);
    }

    disconnectedCallback() {
      this.activation = (this.activation || 0) + 1;
      clearTimeout(this.prefetchTimer);
      this.removeEventListener('click', this.onChoiceClick);
      this.removeEventListener('pointerover', this.onChoiceIntent);
      this.removeEventListener('focusin', this.onChoiceIntent);
      window.removeEventListener('popstate', this.handlePopState);
    }

    markets(panel) {
      return String(panel.dataset.markets || this.fallback).split(',').map((value) => value.trim().toUpperCase()).filter(Boolean);
    }

    isEligible(panel) {
      const markets = this.markets(panel);
      return markets.includes(this.country) || markets.includes(this.fallback);
    }

    eligiblePanels() {
      return this.panels.filter((panel) => this.isEligible(panel));
    }

    preferredMarket(candidates) {
      return candidates.find((panel) => this.markets(panel).includes(this.country))
        || candidates.find((panel) => this.markets(panel).includes(this.fallback));
    }

    findPanel(fit, side, color = this.color) {
      const candidates = this.eligiblePanels().filter((panel) => panel.dataset.fit === fit && panel.dataset.side === side && panel.dataset.color === color);
      return this.preferredMarket(candidates.filter(panel => (panel.dataset.ink || '') === this.ink)) || this.preferredMarket(candidates);
    }

    findInkPanel(ink) {
      const candidates = this.eligiblePanels().filter(panel => panel.dataset.fit === this.fit && panel.dataset.side === this.side && panel.dataset.ink === ink);
      return this.preferredMarket(candidates.filter(panel => panel.dataset.color === this.color)) || this.preferredMarket(candidates);
    }

    firstSide(fit, color = this.color) {
      return this.findPanel(fit, 'front', color) ? 'front' : (this.eligiblePanels().find((panel) => panel.dataset.fit === fit && panel.dataset.color === color)?.dataset.side || 'front');
    }

    firstFit(family, preferredSide = 'front', color = this.color) {
      const eligible = this.eligiblePanels();
      return eligible.find((panel) => panel.dataset.family === family && panel.dataset.side === preferredSide && panel.dataset.color === color)?.dataset.fit
        || eligible.find((panel) => panel.dataset.family === family && panel.dataset.color === color)?.dataset.fit
        || eligible[0]?.dataset.fit
        || '';
    }

    firstColor(fit, side) {
      return this.findPanel(fit, side, 'black') ? 'black' : (this.eligiblePanels().find((panel) => panel.dataset.fit === fit && panel.dataset.side === side)?.dataset.color || 'black');
    }

    showUnavailable() {
      this.panels.forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-active');
      });
      if (this.unavailable) this.unavailable.hidden = false;
      this.dataset.marketStatus = 'unavailable';
    }

    async hydratePanel(panel) {
      if (panel.dataset.needsVariantHydration !== 'true') return true;
      if (panel.variantHydrationPromise) return panel.variantHydrationPromise;

      const productForm = qs('apex-product-form', panel);
      if (!productForm || !panel.dataset.productUrl) return false;
      productForm.setLoading(true);

      panel.variantHydrationPromise = (async () => {
        try {
          const productUrl = new URL(panel.dataset.productUrl, window.location.origin);
          productUrl.pathname = `${productUrl.pathname.replace(/\.js$/, '')}.js`;
          productUrl.search = '';
          const response = await fetch(productUrl, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error(`Product request failed with ${response.status}`);
          const productData = await response.json();
          if (!Array.isArray(productData.variants) || !productData.variants.length) throw new Error('Product response has no variants');
          productForm.hydrateVariants(productData.variants);
          panel.dataset.needsVariantHydration = 'false';
          panel.dataset.variantHydration = 'complete';
          return true;
        } catch (error) {
          panel.dataset.variantHydration = 'failed';
          console.warn('Artistic Apex could not load the selected garment.', error);
          return false;
        } finally {
          productForm.setLoading(false);
        }
      })();

      return panel.variantHydrationPromise;
    }

    async activate(updateUrl, fallbackUrl = '') {
      const activation = this.activation = (this.activation || 0) + 1;
      const previousPanel = this.panels.find(item => !item.hidden);
      const previousForm = previousPanel && qs('apex-product-form', previousPanel);
      const previousSelection = previousForm?.selectionSnapshot?.();
      // A direct ink URL can require a different garment color.
      const requestedInkPanel = this.ink && this.findInkPanel(this.ink);
      if (!updateUrl && new URLSearchParams(window.location.search).has('ink') && requestedInkPanel) this.color = requestedInkPanel.dataset.color;
      let panel = this.findPanel(this.fit, this.side, this.color);
      if (!panel) {
        const eligible = this.eligiblePanels();
        if (!eligible.length) {
          this.showUnavailable();
          return;
        }
        this.fit = this.firstFit(this.family, this.side, this.color);
        if (!this.findPanel(this.fit, this.side, this.color)) this.color = this.firstColor(this.fit, this.side);
        panel = this.findPanel(this.fit, this.side, this.color) || this.findPanel(this.fit, this.firstSide(this.fit, this.color), this.color) || eligible[0];
        this.fit = panel.dataset.fit;
        this.side = panel.dataset.side;
        this.color = panel.dataset.color;
      }
      this.family = panel.dataset.family;
      this.ink = panel.dataset.ink || '';
      if (this.unavailable) this.unavailable.hidden = true;
      this.dataset.marketStatus = 'available';

      if (panel.dataset.panelDeferred === 'true') {
        this.setAttribute('aria-busy', 'true');
        previousForm?.setLoading?.(true);
        const loaded = await this.loadPanel(panel);
        if (activation !== this.activation || !this.isConnected) return;
        this.removeAttribute('aria-busy');
        previousForm?.setLoading?.(false);
        previousForm?.updateVariant?.(false);
        if (!loaded) {
          window.location.assign(fallbackUrl || panel.dataset.productUrl);
          return;
        }
      } else {
        this.removeAttribute('aria-busy');
        previousForm?.setLoading?.(false);
        previousForm?.updateVariant?.(false);
      }
      this.refreshLinks();

      this.panels.forEach((item) => {
        const active = item === panel;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
      });
      this.placeRatingBlocks(panel);

      this.familyLinks.forEach((link) => {
        const active = link.dataset.family === this.family;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
        const firstFit = this.firstFit(link.dataset.family, this.side, this.color);
        const matching = this.findPanel(firstFit, this.side, this.color) || this.findPanel(firstFit, this.firstSide(firstFit, this.color), this.color);
        if (matching) link.href = matching.dataset.productUrl;
      });

      this.fitLinks.forEach((link) => {
        const active = link.dataset.fit === this.fit;
        link.hidden = link.dataset.family !== this.family;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
        const matching = this.findPanel(link.dataset.fit, this.side, this.color) || this.findPanel(link.dataset.fit, this.firstSide(link.dataset.fit, this.color), this.color);
        if (matching) link.href = matching.dataset.productUrl;
      });

      this.sideLinks.forEach((link) => {
        const matching = this.findPanel(this.fit, link.dataset.side, this.color);
        const active = link.dataset.side === this.side;
        link.classList.toggle('is-active', active);
        link.classList.toggle('is-disabled', !matching);
        link.setAttribute('aria-selected', String(active));
        link.setAttribute('aria-disabled', String(!matching));
        if (matching) link.href = matching.dataset.productUrl;
      });

      this.colorLinks.forEach((link) => {
        const matching = this.findPanel(this.fit, this.side, link.dataset.color);
        const active = link.dataset.color === this.color;
        link.classList.toggle('is-active', active);
        link.classList.toggle('is-disabled', !matching);
        link.setAttribute('aria-selected', String(active));
        link.setAttribute('aria-disabled', String(!matching));
        if (matching) link.href = matching.dataset.productUrl;
      });

      this.inkLinks.forEach(link => {
        const matching = this.findInkPanel(link.dataset.ink);
        link.classList.toggle('is-active', link.dataset.ink === this.ink);
        link.classList.toggle('is-disabled', !matching);
        link.setAttribute('aria-selected', String(link.dataset.ink === this.ink));
        link.setAttribute('aria-disabled', String(!matching));
        if (matching) link.href = matching.dataset.productUrl;
      });

      if (updateUrl) {
        const url = new URL(this.dataset.primaryUrl || window.location.pathname, window.location.origin);
        if (this.dataset.coupleRole) url.searchParams.set('role', this.closest('apex-couple')?.view === 'both' ? 'both' : this.dataset.coupleRole);
        url.searchParams.set('family', this.family);
        url.searchParams.set('fit', this.fit);
        url.searchParams.set('side', this.side);
        url.searchParams.set('color', this.color);
        if (this.ink) url.searchParams.set('ink', this.ink);
        else url.searchParams.delete('ink');
        window.history.pushState({ family: this.family, fit: this.fit, side: this.side, color: this.color, ink: this.ink }, '', url);
      }

      const hydrated = await this.hydratePanel(panel);
      if (activation !== this.activation) return;
      if (!hydrated) {
        window.location.assign(fallbackUrl || panel.dataset.productUrl);
        return;
      }
      const form = qs('apex-product-form', panel);
      if (updateUrl && previousPanel !== panel) form?.restoreSelection?.(previousSelection);
      form?.selectVariantFromUrl?.();
      if (this.dataset.coupleRole) this.dispatchEvent(new CustomEvent('apex:offering-ready', { bubbles: true }));
    }
  }

  class ApexProductGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galleryReady === 'true') return;
      this.dataset.galleryReady = 'true';
      this.slides = qsa('[data-gallery-slide]', this);
      this.thumbs = qsa('[data-gallery-thumb]', this);
      this.lightbox = qs('[data-gallery-lightbox]', this);
      this.lightboxSlides = qsa('[data-lightbox-slide]', this);
      this.lightboxImages = qsa('.product-lightbox__image', this);
      this.indexLabel = qs('[data-gallery-index]', this);
      this.lightboxIndexLabel = qs('[data-lightbox-index]', this);
      this.viewport = qs('.product-gallery__viewport', this);
      this.lightboxViewport = qs('.product-lightbox__viewport', this);
      if (!this.slides.length) return;

      this.slides.forEach((slide, index) => slide.addEventListener('click', () => this.open(index)));
      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.setActive(index)));
      qs('[data-gallery-prev]', this)?.addEventListener('click', () => this.step(-1));
      qs('[data-gallery-next]', this)?.addEventListener('click', () => this.step(1));
      qs('[data-lightbox-prev]', this)?.addEventListener('click', () => this.step(-1));
      qs('[data-lightbox-next]', this)?.addEventListener('click', () => this.step(1));
      qs('[data-gallery-close]', this)?.addEventListener('click', () => this.close());

      this.lightboxImages.forEach((image) => {
        image.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggleZoom(image, event);
        });
        image.addEventListener('mousemove', (event) => {
          if (image.classList.contains('is-zoomed')) this.setZoomOrigin(image, event);
        });
      });

      this.lightbox?.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.close();
      });
      this.lightbox?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.lightbox?.addEventListener('close', () => this.afterClose());
      this.bindSwipe(this.viewport);
      this.bindSwipe(this.lightboxViewport);
      this.onKeydown = (event) => this.handleKeydown(event);
      document.addEventListener('keydown', this.onKeydown);

      const currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active') || !slide.hidden);
      this.setActive(currentIndex >= 0 ? currentIndex : 0);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove('product-lightbox-open');
    }

    normalize(index) {
      if (index < 0) return this.slides.length - 1;
      if (index >= this.slides.length) return 0;
      return index;
    }

    activeIndex() {
      const index = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      return index >= 0 ? index : 0;
    }

    setActive(nextIndex) {
      const index = this.normalize(nextIndex);
      this.resetZoom();
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.lightboxSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      if (this.indexLabel) this.indexLabel.textContent = String(index + 1);
      if (this.lightboxIndexLabel) this.lightboxIndexLabel.textContent = String(index + 1);
    }

    selectMedia(mediaId) {
      if (!mediaId) return;
      const index = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) this.setActive(index);
    }

    step(delta) {
      this.setActive(this.activeIndex() + delta);
    }

    open(index = this.activeIndex()) {
      if (!this.lightbox) return;
      this.lastFocus = document.activeElement;
      this.setActive(index);
      if (typeof this.lightbox.showModal === 'function') {
        if (!this.lightbox.open) this.lightbox.showModal();
      } else {
        this.lightbox.setAttribute('open', 'open');
      }
      document.body.classList.add('product-lightbox-open');
      qs('[data-gallery-close]', this.lightbox)?.focus();
    }

    close() {
      if (!this.lightbox) return;
      if (typeof this.lightbox.close === 'function' && this.lightbox.open) this.lightbox.close();
      else {
        this.lightbox.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      this.resetZoom();
      document.body.classList.remove('product-lightbox-open');
      if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    setZoomOrigin(image, event) {
      const rect = image.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
      image.style.setProperty('--zoom-x', `${x}%`);
      image.style.setProperty('--zoom-y', `${y}%`);
    }

    toggleZoom(image, event) {
      const willZoom = !image.classList.contains('is-zoomed');
      this.resetZoom();
      if (!willZoom) return;
      this.setZoomOrigin(image, event);
      image.classList.add('is-zoomed');
      this.lightbox?.classList.add('is-zoomed');
    }

    resetZoom() {
      this.lightboxImages?.forEach((image) => {
        image.classList.remove('is-zoomed');
        image.style.removeProperty('--zoom-x');
        image.style.removeProperty('--zoom-y');
      });
      this.lightbox?.classList.remove('is-zoomed');
    }

    bindSwipe(surface) {
      if (!surface || this.slides.length < 2) return;
      let startX = 0;
      let startY = 0;
      surface.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      surface.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;
        if (Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25) this.step(deltaX < 0 ? 1 : -1);
      }, { passive: true });
    }

    handleKeydown(event) {
      if (!this.lightbox?.open) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowRight' && this.slides.length > 1) this.step(1);
      if (event.key === 'ArrowLeft' && this.slides.length > 1) this.step(-1);
    }
  }

  document.addEventListener('apex:couple-added', () => refreshCartDrawer(true).catch(() => {}));

  class ApexProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      this.variantInput = qs('[data-variant-id]', this);
      this.button = qs('[data-add-to-cart]', this);
      this.label = qs('[data-add-label]', this);
      this.panel = this.closest('[data-offering-panel]');
      try {
        this.variants = JSON.parse(qs('[data-product-variants]', this)?.textContent || '[]');
      } catch (_) {
        this.variants = [];
      }
      this.optionGroups = qsa('[data-product-option-group]', this);
      this.selectVariantFromUrl();
      qsa('[data-option-input]', this).forEach((input) => input.addEventListener('change', () => this.updateVariant(true)));
      qsa('[data-quantity-minus]', this).forEach((button) => button.addEventListener('click', () => this.changeQuantity(-1)));
      qsa('[data-quantity-plus]', this).forEach((button) => button.addEventListener('click', () => this.changeQuantity(1)));
      this.form?.addEventListener('submit', (event) => this.submit(event));
      this.updateVariant(false);
    }

    setLoading(loading) {
      if (!this.button || !this.label) return;
      this.button.disabled = loading || this.button.disabled;
      this.button.toggleAttribute('aria-busy', loading);
      if (loading) this.label.textContent = 'Loading…';
    }

    hydrateVariants(variants) {
      this.variants = variants;
      const variantData = qs('[data-product-variants]', this);
      if (variantData) variantData.textContent = JSON.stringify(variants);
      this.selectVariantFromUrl();
      this.updateVariant(false);
    }

    selectVariantFromUrl() {
      const searchParams = new URLSearchParams(window.location.search);
      const requestedId = searchParams.get('variant');
      if (!requestedId || !this.variants?.length) return;
      const requested = this.variants.find((variant) => String(variant.id) === String(requestedId));
      if (!requested) return;
      this.optionGroups.forEach((group, index) => {
        const input = qsa('[data-option-input]', group).find((candidate) => candidate.value === requested.options[index]);
        if (input) input.checked = true;
      });
      this.updateVariant(false);
    }

    changeQuantity(delta) {
      const input = qs('[data-quantity-input]', this);
      if (!input) return;
      input.value = Math.max(1, Number(input.value || 1) + delta);
    }

    selectedOptions() {
      return this.optionGroups.map((group) => qs('[data-option-input]:checked', group)?.value || '');
    }

    selectionSnapshot() {
      return Object.fromEntries(this.optionGroups.map(group => [String(group.dataset.optionName || '').toLowerCase(), qs('[data-option-input]:checked', group)?.value]));
    }

    restoreSelection(snapshot) {
      if (!snapshot) return;
      const selected = this.selectedOptions();
      const desired = this.optionGroups.map((group, index) => {
        const name = String(group.dataset.optionName || '').toLowerCase();
        return /^(size|sizes|größe|grösse)$/.test(name) ? snapshot[name] || selected[index] : selected[index];
      });
      if (!this.variants.some(variant => variant.options.every((value, index) => value === desired[index]))) return;
      this.optionGroups.forEach((group, index) => {
        const input = qsa('[data-option-input]', group).find(candidate => candidate.value === desired[index]);
        if (input) input.checked = true;
      });
      this.updateVariant(true);
    }

    updateOptionAvailability(selected) {
      this.optionGroups.forEach((group, groupIndex) => {
        const current = qs('[data-option-input]:checked', group);
        const currentLabel = qs('[data-option-current]', group);
        if (currentLabel) currentLabel.textContent = current?.value || '';
        qsa('[data-option-input]', group).forEach((input) => {
          const candidate = [...selected];
          candidate[groupIndex] = input.value;
          const available = this.variants.some((variant) => variant.available && variant.options.every((value, index) => value === candidate[index]));
          input.disabled = !available && !input.checked;
          input.nextElementSibling?.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateVariant(updateUrl = false) {
      if (!this.variants.length) return;
      const selected = this.selectedOptions();
      const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
      this.updateOptionAvailability(selected);
      if (!variant) {
        this.button.disabled = true;
        this.label.textContent = locale.unavailable || 'Unavailable';
        return;
      }
      this.variantInput.value = variant.id;
      this.button.disabled = !variant.available;
      this.label.textContent = variant.available ? (locale.addToCart || 'Add to cart') : (locale.soldOut || 'Sold out');
      const price = qs('[data-product-price]', this.panel);
      if (price) price.textContent = money(variant.price, this.panel?.dataset.currency);
      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      qs('apex-product-gallery', this.panel)?.selectMedia(mediaId);
      if (updateUrl && !this.panel?.hidden) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
      }
    }

    async submit(event) {
      if (!settings.ajaxCart) return;
      event.preventDefault();
      if (!this.form || this.button.disabled) return;
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      try {
        const response = await fetch(routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || locale.addError);
        await refreshCartDrawer(true);
        showToast(locale.added || 'Added to cart.');
      } catch (error) {
        showToast(error.message || locale.addError || 'This item could not be added.');
      } finally {
        this.button.removeAttribute('aria-busy');
        this.updateVariant();
      }
    }
  }

  class ApexCollectionFilters extends HTMLElement {
    connectedCallback() {
      this.panel = qs('[data-filters-panel]', this);
      this.openButton = qs('[data-filters-open]', this.closest('.main-collection, .main-search'));
      this.closeButtons = qsa('[data-filters-close]', this);
      this.mobileQuery = window.matchMedia('(max-width: 899px)');
      if (!this.panel || !this.openButton) return;

      this.handleOpen = () => this.open();
      this.handleClose = () => this.close();
      this.handleKeydown = (event) => this.onKeydown(event);
      this.handleViewport = () => {
        if (!this.mobileQuery.matches) this.close(false);
      };

      this.openButton.addEventListener('click', this.handleOpen);
      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
      document.addEventListener('keydown', this.handleKeydown);
      this.mobileQuery.addEventListener?.('change', this.handleViewport);
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.handleOpen);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleClose));
      document.removeEventListener('keydown', this.handleKeydown);
      this.mobileQuery?.removeEventListener?.('change', this.handleViewport);
    }

    open() {
      if (!this.mobileQuery.matches) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.openButton.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('role', 'dialog');
      this.panel.setAttribute('aria-modal', 'true');
      setBodyLock(true);
      qs('[data-filters-close]', this.panel)?.focus();
    }

    close(restoreFocus = true) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.openButton.setAttribute('aria-expanded', 'false');
      this.panel.removeAttribute('role');
      this.panel.removeAttribute('aria-modal');
      setBodyLock(false);
      if (restoreFocus && this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
    }

    onKeydown(event) {
      if (!this.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = qsa('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),summary,[tabindex]:not([tabindex="-1"])', this.panel)
        .filter((element) => !element.hidden && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class ApexProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.recommendationsReady === 'true') return;
      this.dataset.recommendationsReady = 'true';
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '0px 0px 300px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = qs('apex-product-recommendations', parsed);
        if (replacement && qs('[data-recommendation-content]', replacement)) {
          this.innerHTML = replacement.innerHTML;
          this.dataset.recommendationSource = qs('[data-recommendation-content]', replacement)?.dataset.recommendationSource || 'native';
        }
      } catch (_) {
        this.dataset.recommendationSource = 'fallback';
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('apex-header', ApexHeader);
  customElements.define('apex-cart-drawer', ApexCartDrawer);
  customElements.define('predictive-search', PredictiveSearch);
  customElements.define('apex-product-gallery', ApexProductGallery);
  customElements.define('apex-product-form', ApexProductForm);
  customElements.define('apex-product-configurator', ApexProductConfigurator);
  customElements.define('apex-collection-filters', ApexCollectionFilters);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-product-info-target]');
    if (!trigger) return;
    const target = trigger.dataset.productInfoTarget;
    if (!['size-guide', 'delivery'].includes(target)) return;
    const panel = trigger.closest('[data-offering-panel]');
    const details = panel?.querySelector(`[data-product-info="${target}"]`);
    if (!details) return;
    details.open = true;
    details.querySelector('summary')?.focus({ preventScroll: true });
    details.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });

  customElements.define('apex-product-recommendations', ApexProductRecommendations);

  document.addEventListener('click', (event) => {
    const cookiePreferences = event.target.closest('[data-cookie-preferences]');
    if (cookiePreferences) {
      event.preventDefault();
      openCookiePreferences(cookiePreferences);
      return;
    }

    const openCart = event.target.closest('[data-cart-drawer-open]');
    if (openCart) {
      event.preventDefault();
      qs('[data-cart-drawer]')?.open();
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!quantityButton && !removeButton) return;
    const line = (quantityButton || removeButton).closest('[data-cart-line]');
    if (!line || line.getAttribute('aria-busy') === 'true') return;
    event.preventDefault();
    const currentQuantity = Number(line.dataset.lineQuantity || 0);
    const nextQuantity = removeButton ? 0 : currentQuantity + Number(quantityButton.dataset.cartQuantityChange || 0);
    changeCartLine(line, nextQuantity);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-line-input]');
    if (!input || input.readOnly) return;
    const line = input.closest('[data-cart-line]');
    if (!line || line.getAttribute('aria-busy') === 'true') return;
    if (String(input.value).trim() === '' || !Number.isFinite(Number(input.value))) {
      input.value = line.dataset.lineQuantity || '1';
      return;
    }
    const nextQuantity = Math.max(0, Math.floor(Number(input.value)));
    if (nextQuantity === Number(line.dataset.lineQuantity || 0)) return;
    changeCartLine(line, nextQuantity);
  });

  class ApexCardInks extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      try {
        const data = qs('[data-card-previews]', this);
        this.previews = JSON.parse(data?.content?.textContent || data?.textContent || '[]');
      } catch (_) {
        return;
      }
      if (!Array.isArray(this.previews) || !this.previews.length) return;
      this.initialized = true;
      this.request = 0;
      this.selection = this.previews.find(preview => preview.ink === this.dataset.ink && preview.color === this.dataset.color) || this.previews[0];
      this.updateOptions(this.selection);
      this.addEventListener('click', event => {
        const option = event.target.closest('[data-card-ink], [data-card-shirt]');
        if (!option || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.select(option);
      });
      this.addEventListener('keydown', event => {
        if (event.key === ' ' && event.target.matches('[data-card-ink], [data-card-shirt]')) {
          event.preventDefault();
          this.select(event.target);
        }
      });
    }

    previewFor(option, selection = this.pendingSelection || this.selection) {
      const isInk = option.hasAttribute('data-card-ink');
      const matches = this.previews.filter(preview => isInk ? preview.ink === option.dataset.cardInk : preview.color === option.dataset.cardShirt);
      return matches.find(preview => isInk ? preview.color === selection.color : preview.ink === selection.ink) || matches[0];
    }

    previewLabel(preview) {
      return `${preview.colorLabel} shirt${preview.inkLabel ? ` with ${preview.inkLabel} ink` : ''}`;
    }

    updateOptions(selection) {
      this.dataset.ink = selection.ink;
      this.dataset.color = selection.color;
      qsa('[data-card-ink], [data-card-shirt]', this).forEach(option => {
        const preview = this.previewFor(option, selection);
        if (!preview) return;
        option.href = preview.url;
        const isInk = option.hasAttribute('data-card-ink');
        const active = isInk ? option.dataset.cardInk === selection.ink : option.dataset.cardShirt === selection.color;
        option.setAttribute('aria-pressed', String(active));
        option.setAttribute('aria-label', this.previewLabel(preview));
        option.title = this.previewLabel(preview);
      });
    }

    async select(option) {
      const selected = this.previewFor(option);
      if (!selected) return;
      const request = ++this.request;
      this.pendingSelection = selected;
      const card = this.closest('.product-card');
      const image = qs('.product-card__image', card);
      const status = qs('[data-card-ink-status]', this);
      this.setAttribute('aria-busy', 'true');
      if (status) status.textContent = `Loading ${this.previewLabel(selected)} preview.`;
      try {
        if (!image || !selected.image) throw new Error('Preview unavailable');
        await new Promise((resolve, reject) => {
          const preview = new Image();
          const timer = setTimeout(() => reject(new Error('Preview timed out')), 10000);
          preview.onload = () => { clearTimeout(timer); resolve(); };
          preview.onerror = () => { clearTimeout(timer); reject(new Error('Preview unavailable')); };
          preview.sizes = image.sizes;
          preview.srcset = selected.srcset || '';
          preview.src = selected.image;
        });
        if (request !== this.request || !this.isConnected) return;
        image.srcset = selected.srcset || '';
        image.src = selected.image;
        image.alt = selected.alt;
        qsa('[data-card-product-link]', card).forEach(link => { link.href = selected.url; });
        const price = qs('[data-card-price]', card);
        if (price) price.textContent = selected.price;
        this.selection = selected;
        this.updateOptions(selected);
        if (status) status.textContent = `${this.previewLabel(selected)} selected.`;
      } catch (_) {
        if (request === this.request && this.isConnected) window.location.assign(selected.url);
      } finally {
        if (request === this.request) {
          this.pendingSelection = null;
          this.removeAttribute('aria-busy');
        }
      }
    }
  }
  if (!customElements.get('apex-card-inks')) customElements.define('apex-card-inks', ApexCardInks);

  qsa('[data-sort-select]').forEach((select) => select.addEventListener('change', (event) => event.target.form?.submit()));
  cleanLegacyCartDetails();
})();
